import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import type {
  AracDetay, DovizOzetSatiri, Hizmet, KasaHareketiOzet, KasaOzeti, Urun,
} from '@nero/shared';
import {
  ANA_PARA_BIRIMI, ARALIK_ADI, ARALIK_ANAHTARLARI, ODEME_TIPI_ADI, ODEME_TIPLERI,
  aralikMetni, araligiCoz, bugun, kurus, para, plakaGoster, tarihGoster,
} from '@nero/shared';
import type { AralikAnahtari, ParaBirimi } from '@nero/shared';
import { api, useEylem, useVeri } from '../lib/veri.js';
import { useOturum } from '../lib/oturum.js';
import { useBildirim } from '../lib/bildirim.js';
import { sorgu } from '../lib/api.js';
import { BosDurum, HataKutusu, Kart, Olcum, SayfaBaslik, Rozet, UyariSerit, Yukleniyor } from '../bilesenler/temel.js';
import { Girdi, ParaGirdi, Secim, SecenekSerit } from '../bilesenler/form.js';
import { OnayPenceresi, Pencere } from '../bilesenler/Pencere.js';
import { MusteriSecici } from '../bilesenler/MusteriSecici.js';
import { Simge } from '../bilesenler/Simge.js';
import { KasaSekmeleri } from './Proforma.js';

export function Kasa() {
  const [aralik, setAralik] = useState<AralikAnahtari>('bu_ay');
  const [tekGun, setTekGun] = useState(bugun());
  const [bas, setBas] = useState(bugun());
  const [bit, setBit] = useState(bugun());
  const [duzenlenen, setDuzenlenen] = useState<KasaHareketiOzet | 'yeni' | null>(null);
  const [silinecek, setSilinecek] = useState<KasaHareketiOzet | null>(null);
  const { yetkili } = useOturum();
  const bildir = useBildirim();
  /* KASA DEFTERİ YALNIZCA `kasa.goruntule` OLANA AÇIKTIR.
     Personel kasaya girdi/çıktı YAZABİLİR (`kasa.kayit`) ama defteri
     okuyamaz: ne hareket listesini, ne toplamları, ne bakiyeyi. Bu
     ayrım izinler.ts'te tanımlıdır, burada yalnız uygulanır. */
  const finansGor = yetkili('kasa.goruntule');
  const kayitYapar = yetkili('kasa.islem') || yetkili('kasa.kayit');

  const f = useMemo(() => araligiCoz(aralik, tekGun, bas, bit), [aralik, tekGun, bas, bit]);
  /* DEFTER YALNIZCA YÖNETİCİYE ÇEKİLİR.
     `finansGor` yoksa istek HİÇ YAPILMAZ: sunucu zaten 403 döndürürdü ve
     personelin kullanamayacağı bir uca boşuna istek atmak, ekranda
     anlamsız bir hata kutusu çıkarmaktan başka işe yaramaz. */
  const { veri, yukleniyor, hata, yenile } = useVeri<KasaHareketiOzet[]>(
    finansGor ? `/kasa${sorgu({ baslangic: f.baslangic, bitis: f.bitis })}` : null,
  );

  /* DÖVİZ KIRILIMI SUNUCUDAN GELİR, listeden hesaplanmaz.
     Liste ucu 500 satırla sınırlı (tarayıcıyı kilitlememek için); toplamı
     o kesilmiş listeden çıkarmak, aralık büyüdükçe SESSİZCE eksik rakam
     gösterirdi. */
  const dovizDurum = useVeri<DovizOzetSatiri[]>(
    finansGor ? `/kasa/doviz${sorgu({ baslangic: f.baslangic, bitis: f.bitis })}` : null,
  );
  const doviz = dovizDurum.veri ?? [];

  const ozet = useMemo<KasaOzeti>(() => {
    const g = (veri ?? []).filter((k) => k.tur === 'Girdi').reduce((t, k) => t + k.tutar, 0);
    const c = (veri ?? []).filter((k) => k.tur === 'Cikti').reduce((t, k) => t + k.tutar, 0);
    return { girdi: g, cikti: c, net: g - c, adet: veri?.length ?? 0 };
  }, [veri]);

  /**
   * LİSTE VE DÖVİZ ŞERİDİ BİRLİKTE TAZELENİR.
   *
   * İkisi AYRI uçlardan gelir (`/kasa` ve `/kasa/doviz`). Yalnız listeyi
   * tazelemek, yeni eklenen 400 €'yu tabloda gösterip döviz şeridinde
   * göstermezdi — kullanıcı "euro toplamı yanlış" derdi. Tarayıcı testi
   * tam bu yüzden şeridi bulamadı ve hatayı yakaladı.
   */
  const tumunuTazele = () => { yenile(); dovizDurum.yenile(); };

  const sil = useEylem(async (id: number) => {
    await api.sil(`/kasa/${id}`);
    tumunuTazele();
    setSilinecek(null);
    bildir('Kasa kaydı çöpe taşındı, ürünse stok iade edildi');
  });

  /* ─────────── PERSONEL GÖRÜNÜMÜ: YALNIZCA KAYIT ───────────
     Defteri okuyamayan kullanıcıya liste, tarih aralığı, toplam kutusu
     ya da düzenle/sil düğmesi ÇİZİLMEZ. Ekranda tek iş vardır: hareket
     eklemek. Böylece "gizlenmiş ama veri yine indi" durumu da olmaz —
     veri hiç istenmez (bkz. yukarıdaki `useVeri(null)`).
     Kilit: araclar/tarayici-testi/kasaStokMobil.tarayici.test.js */
  if (!finansGor) {
    return (
      <div className="sayfa">
        <SayfaBaslik baslik="Kasa" alt="Gelir ve gider kaydı" />
        <Kart>
          <div className="kart-govde sutun" style={{ gap: 'var(--b4)' }}>
            <div style={{ color: 'var(--metin-2)', fontSize: 'var(--p-14)', lineHeight: 1.5 }}>
              Kasaya <b>girdi</b> ve <b>çıktı</b> kaydı ekleyebilirsiniz.
              Kasa defteri, günlük/aylık toplamlar ve bakiye yöneticiye özeldir.
            </div>
            {kayitYapar && (
              <div>
                <button className="btn btn-birincil" onClick={() => setDuzenlenen('yeni')}>
                  <Simge ad="arti" className="simge" /> Kasa hareketi ekle
                </button>
              </div>
            )}
          </div>
        </Kart>
        {duzenlenen && (
          <HareketPenceresi
            mevcut={duzenlenen === 'yeni' ? undefined : duzenlenen}
            kapat={() => setDuzenlenen(null)}
            kaydedildi={() => { setDuzenlenen(null); bildir('Hareket kaydedildi'); }}
          />
        )}
      </div>
    );
  }

  return (
    <div className="sayfa">
      <SayfaBaslik baslik="Kasa" alt="Gelir, gider ve tahsilat hareketleri">
        {kayitYapar && (
          <button className="btn btn-birincil" onClick={() => setDuzenlenen('yeni')}>
            <Simge ad="arti" className="simge" /> Hareket ekle
          </button>
        )}
      </SayfaBaslik>

      <KasaSekmeleri etkin="kasa" />

      {finansGor && (
        <div className="izgara izgara-3" style={{ marginBottom: 'var(--b5)' }}>
          <Olcum etiket="Girdi" deger={para(ozet.girdi)} vurgu="olumlu" alt={`${ozet.adet} hareket`} />
          <Olcum etiket="Gider" deger={para(ozet.cikti)} />
          <Olcum etiket="Net" deger={para(ozet.net)} buyuk vurgu={ozet.net >= 0 ? 'olumlu' : 'uyari'} />
        </div>
      )}

      {/* DÖVİZ KIRILIMI — TL toplamının YERİNE değil, YANINDA.
          Yukarıdaki üç kutu işletmenin toplam nakdini TL olarak verir ve
          döviz hareketleri de TL karşılığıyla oraya dâhildir. Buradaki
          satırlar "kasaya kaç euro girdi" sorusunun ayrı cevabıdır. */}
      {finansGor && doviz.length > 0 && (
        <div className="doviz-serit" style={{ marginBottom: 'var(--b5)' }}>
          <span className="doviz-serit-baslik">Döviz</span>
          {doviz.map((d) => (
            <span key={d.para_birimi} className="doviz-serit-oge">
              <strong>{para(d.net, d.para_birimi)}</strong>
              <span className="ikincil">
                {' '}net · {para(d.girdi, d.para_birimi)} giriş
                {d.cikti > 0 && <> · {para(d.cikti, d.para_birimi)} çıkış</>}
                {' '}· {d.adet} hareket
              </span>
            </span>
          ))}
          <span className="doviz-serit-not">TL karşılıkları yukarıdaki toplamlara dâhildir.</span>
        </div>
      )}

      <div className="sutun" style={{ gap: 'var(--b3)', marginBottom: 'var(--b4)' }}>
        <SecenekSerit
          deger={aralik}
          secenekler={ARALIK_ANAHTARLARI.map((a) => ({ deger: a, ad: ARALIK_ADI[a] }))}
          onChange={setAralik}
        />
        {aralik === 'tek_gun' && (
          <div style={{ width: 190 }}>
            <Girdi etiket="Gün" type="date" value={tekGun} onChange={setTekGun} />
          </div>
        )}
        {aralik === 'aralik' && (
          <div className="satir" style={{ gap: 'var(--b3)', flexWrap: 'wrap', alignItems: 'flex-end' }}>
            <div style={{ width: 190 }}>
              <Girdi etiket="Başlangıç" type="date" value={bas} onChange={setBas} max={bit} />
            </div>
            <div style={{ width: 190 }}>
              <Girdi etiket="Bitiş" type="date" value={bit} onChange={setBit} min={bas} />
            </div>
          </div>
        )}
        <div style={{ fontSize: 'var(--p-12)', color: 'var(--metin-3)' }}>
          {aralikMetni(f)} · {ozet.adet} hareket
        </div>
      </div>

      <Kart>
        {hata ? <div className="kart-govde"><HataKutusu hata={hata} yenile={yenile} /></div>
          : yukleniyor ? <div className="kart-govde"><Yukleniyor /></div>
          : !veri || veri.length === 0 ? (
            <BosDurum simge="kasa" baslik="Bu aralıkta hareket yok" aciklama="Farklı bir tarih aralığı seçin veya yeni hareket ekleyin." />
          ) : (
            <div className="tablo-sarma">
              <table className="tablo">
                <thead>
                  <tr>
                    <th>Tarih</th><th>Açıklama</th><th>Müşteri</th>
                    <th>Yöntem</th><th className="sag">Tutar</th>
                    {(yetkili('kasa.islem') || yetkili('kasa.sil')) && <th />}
                  </tr>
                </thead>
                <tbody>
                  {veri.map((k) => (
                    <tr key={k.id}>
                      <td className="ikincil dar">{tarihGoster(k.tarih)}<div style={{ fontSize: 'var(--p-11)' }}>{k.saat}</div></td>
                      <td>
                        <div className="satir" style={{ gap: 6, flexWrap: 'wrap' }}>
                          <span className="ana">{k.kategori || (k.tur === 'Girdi' ? 'Gelir' : 'Gider')}</span>
                          {k.is_emri_no && <Rozet ton="bilgi" sade>İş emrine bağlı</Rozet>}
                        </div>
                        <div className="ikincil">
                          {k.is_emri_no && (
                            <Link to={`/is-emirleri/${k.is_emri_id}`} style={{ textDecoration: 'underline' }}>{k.is_emri_no}</Link>
                          )}
                          {k.urun_ad && <> · {k.urun_ad} × {k.urun_adet}</>}
                          {k.not && <> · {k.not}</>}
                        </div>
                      </td>
                      <td className="ikincil">{k.musteri_ad ?? '—'}</td>
                      <td><Rozet ton="notr" sade>{ODEME_TIPI_ADI[k.odeme_tipi] ?? k.odeme_tipi}</Rozet></td>
                      <td className="sag para" style={{ color: k.tur === 'Girdi' ? 'var(--olumlu)' : 'var(--metin)' }}>
                        {k.tur === 'Girdi' ? '+' : '−'}
                        {/* DÖVİZLİ SATIRDA ÖNCE GİRİLEN PARA GÖRÜNÜR.
                            Kullanıcı "400 €" yazdıysa listede de onu
                            arar; TL karşılığı hemen altında küçük durur.
                            TL satırlarda ikinci satır HİÇ çıkmaz. */}
                        {para(k.doviz_tutar ?? k.tutar, k.para_birimi)}
                        {k.para_birimi && k.para_birimi !== ANA_PARA_BIRIMI && (
                          <div className="ikincil doviz-alt">{para(k.tutar)}</div>
                        )}
                      </td>
                      {(yetkili('kasa.islem') || yetkili('kasa.sil')) && (
                        <td className="dar">
                          <div className="satir">
                            {yetkili('kasa.islem') && (
                              <button className="btn btn-sade btn-simge btn-kucuk" onClick={() => setDuzenlenen(k)}
                                aria-label="Düzenle">
                                <Simge ad="kalem" boyut={14} />
                              </button>
                            )}
                            {yetkili('kasa.sil') && (
                              <button className="btn btn-sade btn-simge btn-kucuk" onClick={() => setSilinecek(k)} aria-label="Sil">
                                <Simge ad="cop" boyut={14} />
                              </button>
                            )}
                          </div>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
      </Kart>

      {duzenlenen && (
        <HareketPenceresi
          mevcut={duzenlenen === 'yeni' ? undefined : duzenlenen}
          kapat={() => setDuzenlenen(null)}
          kaydedildi={() => { setDuzenlenen(null); tumunuTazele(); }}
        />
      )}
      {silinecek && (
        <OnayPenceresi
          baslik="Kasa kaydını sil"
          aciklama={`${para(silinecek.tutar)} tutarındaki kayıt çöpe taşınacak.${silinecek.urun_ad ? ' Kullanılan stok iade edilecek.' : ''}`}
          kapat={() => setSilinecek(null)}
          onayla={() => void sil.yurut(silinecek.id)}
          calisiyor={sil.calisiyor}
          hata={sil.hata}
        />
      )}
    </div>
  );
}

function HareketPenceresi({ mevcut, kapat, kaydedildi }: {
  mevcut?: KasaHareketiOzet; kapat: () => void; kaydedildi: () => void;
}) {
  const bildir = useBildirim();
  const [tur, setTur] = useState<'Girdi' | 'Cikti'>(mevcut?.tur ?? 'Girdi');
  const [tarih, setTarih] = useState(mevcut?.tarih ?? bugun());
  /* DÜZENLEMEDE YAZILAN SAYI GERİ GELİR, TL karşılığı DEĞİL.
     "400 €" girilmiş bir kaydı açınca 20.000 görünseydi, kullanıcı hiçbir
     şey değiştirmeden kaydettiğinde o 20.000 bir kez daha kurla çarpılır
     ve tutar 45 katına çıkardı. */
  const [tutar, setTutar] = useState(mevcut ? String(mevcut.doviz_tutar ?? mevcut.tutar) : '');
  const [odemeTipi, setOdemeTipi] = useState<string>(mevcut?.odeme_tipi ?? 'Nakit');
  const [kategori, setKategori] = useState(mevcut?.kategori ?? '');
  const [musteriId, setMusteriId] = useState(mevcut?.musteri_id ? String(mevcut.musteri_id) : '');
  const [aracId, setAracId] = useState(mevcut?.arac_id ? String(mevcut.arac_id) : '');
  const [hizmetId, setHizmetId] = useState('');
  const [urunId, setUrunId] = useState(mevcut?.urun_id ? String(mevcut.urun_id) : '');
  const [urunAdet, setUrunAdet] = useState(mevcut?.urun_adet ? String(mevcut.urun_adet) : '');
  const [not, setNot] = useState(mevcut?.not ?? '');
  /* Düzenlemede kaydın KENDİ para birimi ve YAZILAN sayısı geri gelir —
     TL karşılığı değil. Aksi hâlde "400 €" olarak girilmiş bir kaydı
     açınca 20.000 görünür ve kaydedince bir kez daha kurla çarpılırdı. */
  const [paraBirimi, setParaBirimi] = useState<ParaBirimi>(mevcut?.para_birimi ?? ANA_PARA_BIRIMI);

  const urunler = useVeri<Urun[]>('/urunler?pasif=0');
  const hizmetler = useVeri<Hizmet[]>('/hizmetler');
  /* Araç listesi seçilen müşteriye göre daralır: yanlış araca kayıt açılamaz. */
  const araclar = useVeri<AracDetay[]>(musteriId ? `/araclar${sorgu({ musteri_id: musteriId })}` : null);
  /* Geçmişte kullanılmış kategori ve notlar öneri olarak çıkar. */
  const oneri = useVeri<{ kategoriler: string[]; notlar: string[] }>('/kasa/oneriler');

  const gecerli = Number(tutar) > 0;

  const { yurut, calisiyor, hata } = useEylem(async () => {
    const govde = {
      /* `tutar` KULLANICININ YAZDIĞI SAYIDIR, TL değil. TL karşılığını
         sunucu hesaplar (bkz. shared/dogrulama.ts · paraBirimi). */
      tarih, tur, tutar: Number(tutar), para_birimi: paraBirimi,
      odeme_tipi: odemeTipi, kategori,
      musteri_id: musteriId ? Number(musteriId) : null,
      arac_id: aracId ? Number(aracId) : null,
      /* İş emri bağlantısı düzenlemede korunur; buradan koparılmaz. */
      is_emri_id: mevcut?.is_emri_id ?? null,
      urun_id: urunId ? Number(urunId) : null,
      urun_adet: urunId && urunAdet ? Number(urunAdet) : null,
      not,
    };
    if (mevcut) await api.guncelle(`/kasa/${mevcut.id}`, govde);
    else await api.gonder('/kasa', govde);
    bildir(mevcut ? 'Kasa hareketi güncellendi' : 'Kasa hareketi eklendi');
    kaydedildi();
  });

  /* Enter ile hızlı kayıt: tutar girildiyse form Enter'a basınca kaydeder.
     Çok satırlı not alanında Enter yeni satır açar, oradan tetiklenmez. */
  function enterIleKaydet(e: React.KeyboardEvent) {
    if (e.key !== 'Enter' || e.shiftKey) return;
    const hedef = e.target as HTMLElement;
    /* Düğme, bağlantı ve çok satırlı alanlarda Enter'ın kendi işi var:
       öneri çipine sekip Enter'a basmak çipi uygulamalı, kaydı değil. */
    if (['TEXTAREA', 'BUTTON', 'A'].includes(hedef.tagName)) return;
    e.preventDefault();
    if (gecerli && !calisiyor) void yurut(undefined as never);
  }

  /* Hizmet seçilince kategori ve tutar hazır gelir; kullanıcı yine değiştirebilir. */
  function hizmetSec(deger: string) {
    setHizmetId(deger);
    const h = hizmetler.veri?.find((x) => String(x.id) === deger);
    if (!h) return;
    setKategori(h.ad);
    /* Hizmet fiyatları TL'dir (hizmet dövize açılmadı). Tutarı doldururken
       para birimi de TL'ye çekilir; yoksa € seçiliyken TL bir rakam
       yazılır ve kullanıcı 1.500 € ödeme kaydetmiş olurdu. */
    if (!Number(tutar) && h.varsayilan_fiyat != null) {
      setParaBirimi(ANA_PARA_BIRIMI);
      setTutar(String(h.varsayilan_fiyat));
    }
  }

  function musteriSec(deger: string) {
    setMusteriId(deger);
    setAracId('');
  }

  /**
   * Girdi ↔ Çıktı geçişinde yalnızca GİRDİ'ye ait alanlar temizlenir.
   * Temizlenmezse ekranda görünmeyen bir ürün seçimi kayda gider ve bir
   * GİDER kaydı sessizce stoktan düşer.
   */
  function turDegistir(yeni: string) {
    setTur(yeni as 'Girdi' | 'Cikti');
    if (yeni === 'Cikti') {
      setUrunId(''); setUrunAdet(''); setHizmetId('');
      setMusteriId(''); setAracId('');
    }
  }

  /**
   * Ürün satışında tutar = adet × satış fiyatı.
   *
   * ÜRÜN KENDİ PARA BİRİMİNDE DOLDURUR. 150 €'ya fiyatlanmış bir parça
   * seçildiğinde tutar 150 ve birim € olur — TL karşılığı değil. Müşteri
   * lirayla öderse kullanıcı ₺ düğmesine basar; o zaman TL fiyat yazılır.
   */
  function urunTutari(
    u: { satis_fiyat: number; satis_doviz?: number; satis_para_birimi?: ParaBirimi } | undefined,
    adetMetni: string,
    birim: ParaBirimi,
  ) {
    const adet = Number(adetMetni) || 0;
    if (!u || adet <= 0) return null;
    const urunBirim = u.satis_para_birimi ?? ANA_PARA_BIRIMI;
    /* Seçilen birim ürünün birimiyle aynıysa ürünün YAZILMIŞ fiyatı,
       değilse TL karşılığı kullanılır. İki farklı dövizi birbirine
       çevirmeye kalkmak (€ fiyatı $ tutara) uydurma olurdu. */
    const fiyat = birim === urunBirim && urunBirim !== ANA_PARA_BIRIMI
      ? Number(u.satis_doviz ?? u.satis_fiyat)
      : Number(u.satis_fiyat || 0);
    return kurus(adet * fiyat);
  }

  function urunSec(deger: string) {
    setUrunId(deger);
    const u = urunler.veri?.find((x) => String(x.id) === deger);
    if (!u) return;
    const adet = urunAdet || '1';
    if (!urunAdet) setUrunAdet(adet);
    const urunBirim = u.satis_para_birimi ?? ANA_PARA_BIRIMI;
    const hesap = urunTutari(u, adet, urunBirim);
    if (hesap != null && (!Number(tutar) || Number(tutar) === Number(u.satis_fiyat))) {
      setParaBirimi(urunBirim);
      setTutar(String(hesap));
    }
    if (!kategori) setKategori('Ürün satışı');
  }

  /** Adet değişince tutarı da güncelle — 3 adet seçilip 1 adetlik para alınmasın. */
  function urunAdetDegistir(deger: string) {
    setUrunAdet(deger);
    const u = urunler.veri?.find((x) => String(x.id) === urunId);
    const hesap = urunTutari(u, deger, paraBirimi);
    if (hesap != null) setTutar(String(hesap));
  }

  /**
   * Para birimi düğmesi SAYIYA DOKUNMAZ.
   *
   * Yazdığın 400'ü € yapmak onu 20.000 ₺'ye çevirmek DEĞİLDİR: kullanıcı
   * "400 euro aldım" diyor. Tek istisna ürün seçili olmasıdır — orada
   * fiyat ürünün kendi fiyatından yeniden doldurulur, çünkü o sayı
   * kullanıcının yazdığı değil, sistemin önerdiği sayıdır.
   */
  function paraBirimiDegistir(yeni: ParaBirimi) {
    setParaBirimi(yeni);
    if (!urunId) return;
    const u = urunler.veri?.find((x) => String(x.id) === urunId);
    const hesap = urunTutari(u, urunAdet || '1', yeni);
    if (hesap != null) setTutar(String(hesap));
  }

  return (
    <Pencere
      baslik={mevcut ? 'Kasa hareketini düzenle' : 'Kasa hareketi'}
      alt={mevcut?.is_emri_no ? `İş emrine bağlı · ${mevcut.is_emri_no}` : 'Enter ile hızlı kaydedebilirsiniz'}
      kapat={kapat}
      altBolum={
        <>
          <button className="btn btn-sade" onClick={kapat} disabled={calisiyor}>Vazgeç</button>
          <button className="btn btn-birincil" onClick={() => void yurut(undefined as never)}
            disabled={calisiyor || !gecerli}>
            {calisiyor ? 'Kaydediliyor…' : 'Kaydet'}
          </button>
        </>
      }
    >
      <div className="sutun" onKeyDown={enterIleKaydet}>
        {mevcut?.is_emri_no && (
          <UyariSerit ton="notr">
            Bu kayıt {mevcut.is_emri_no} iş emrine bağlı. Tutarı değiştirirseniz iş emrinin
            ödeme durumu da otomatik güncellenir.
          </UyariSerit>
        )}
        <SecenekSerit
          deger={tur}
          secenekler={[{ deger: 'Girdi', ad: 'Girdi (gelir)' }, { deger: 'Cikti', ad: 'Çıktı (gider)' }]}
          onChange={turDegistir}
        />
        <div className="izgara izgara-2">
          <Girdi etiket="Tarih" type="date" value={tarih} onChange={setTarih} />
          <ParaGirdi
            etiket="Tutar" deger={tutar} onChange={setTutar} autoFocus
            paraBirimi={paraBirimi} onParaBirimi={paraBirimiDegistir}
          />
        </div>
        <div className="izgara izgara-2">
          <Secim etiket="Ödeme yöntemi" value={odemeTipi} onChange={setOdemeTipi}
            secenekler={ODEME_TIPLERI.map((t) => ({ deger: t, ad: ODEME_TIPI_ADI[t] ?? t }))} />
          <Girdi etiket="Kategori" value={kategori} onChange={setKategori} list="kasa-kategori"
            placeholder={tur === 'Girdi' ? 'Yazılım, ürün satışı…' : 'Kira, elektrik, malzeme…'} duzen="kategoriAdi" />
        </div>
        {tur === 'Girdi' && (
          <>
            <Secim etiket="Hizmet" value={hizmetId} onChange={hizmetSec} bosMetin="Hizmet seçilmedi"
              ipucu="Seçilince kategori ve fiyat dolar"
              secenekler={(hizmetler.veri ?? []).filter((h) => h.aktif === 1).map((h) => ({
                deger: String(h.id),
                ad: h.varsayilan_fiyat != null ? `${h.ad} — ${para(h.varsayilan_fiyat)}` : h.ad,
              }))} />
            <MusteriSecici
              deger={musteriId} onChange={musteriSec}
              baslangicAdi={mevcut?.musteri_ad}
              bosMetin="İsteğe bağlı — yazıp Enter'a basınca kayıtlı müşteri seçilir"
            />
            {musteriId && (
              <Secim etiket="Araç" value={aracId} onChange={setAracId} bosMetin="Araç seçilmedi"
                secenekler={(araclar.veri ?? []).map((a) => ({
                  deger: String(a.id),
                  ad: [plakaGoster(a.plaka), a.marka, a.model].filter(Boolean).join(' · ') || `Araç #${a.id}`,
                }))} />
            )}
            <div className="izgara izgara-2">
              <Secim etiket="Stoktan ürün" value={urunId} onChange={urunSec} bosMetin="Ürün kullanılmadı"
                secenekler={(urunler.veri ?? []).map((u) => ({ deger: String(u.id), ad: `${u.ad} — ${u.adet} ${u.birim}` }))} />
              <Girdi etiket="Adet" type="number" min={0} step="any" value={urunAdet} onChange={urunAdetDegistir} disabled={!urunId} />
            </div>
            {urunId && <UyariSerit ton="notr">Seçilen adet stoktan otomatik düşülecek.</UyariSerit>}
          </>
        )}
        <div>
          <Girdi
            etiket="Not" value={not} onChange={setNot} list="kasa-not"
            placeholder="Kısa açıklama — daha önce yazdıklarınız önerilir"
          duzen="cumle" />
          {(oneri.veri?.notlar.length ?? 0) > 0 && (
            <div className="satir" style={{ gap: 6, flexWrap: 'wrap', marginTop: 6 }}>
              {oneri.veri!.notlar.slice(0, 6).map((n) => (
                <button key={n} type="button" className="btn btn-sade btn-kucuk" onClick={() => setNot(n)}>
                  {n.length > 28 ? `${n.slice(0, 27)}…` : n}
                </button>
              ))}
            </div>
          )}
        </div>
        <datalist id="kasa-kategori">
          {(oneri.veri?.kategoriler ?? []).map((k) => <option key={k} value={k} />)}
        </datalist>
        <datalist id="kasa-not">
          {(oneri.veri?.notlar ?? []).map((n) => <option key={n} value={n} />)}
        </datalist>
        {hata && <UyariSerit ton="kritik">{hata}</UyariSerit>}
      </div>
    </Pencere>
  );
}
